import { useEffect, useState } from 'react';
import WhatsAppMessengerIcons from '../components/live-chat/WhatsAppMessengerIcons';
import { useAdmin } from '../components/context/AdminContext';
import CustomPackageForm from './CustomPackageForm';
import axios from 'axios';
import DestinationGallery from './DestinationGallery';

export default function Home() {
  const [showForm, setShowForm] = useState(false);
  const [packages, setPackages] = useState([]);
  const [guides, setGuides] = useState([]);
  const [aboutPage, setAboutPage] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [loadingAbout, setLoadingAbout] = useState(true);
  const [message, setMessage] = useState('');

  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState('');

  const { getPackages } = useAdmin();

  const [bookingFormData, setBookingFormData] = useState({
    name: '',
    email: '',
    date: '',
    destination: '',
    message: '',
    packageName: ''
  });
  const [bookingPackages, setBookingPackages] = useState([]);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingMessage, setBookingMessage] = useState({ type: '', text: '' });
  const [bookingLoading, setBookingLoading] = useState(true);

  // Load packages from your custom backend
  useEffect(() => {
    const loadPackages = async () => {
      try {
        const packagesData = await getPackages();
        setPackages(packagesData);
      } catch (error) {
        console.error('Error loading packages:', error);
        setMessage('Error loading packages. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPackages();
  }, [getPackages]);

  // Load guides from API
  useEffect(() => {
    const loadGuides = async () => {
      try {
        const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/guides');
        if (response.data.success) {
          const activeGuides = (response.data.guides || [])
            .filter(guide => guide.isActive === true)
            .sort((a, b) => a.order - b.order)
            .slice(0, 4); // সর্বোচ্চ ৪টি guide show করবে
          setGuides(activeGuides);
        }
      } catch (error) {
        console.error('Error loading guides:', error);
        setGuides([]);
      } finally {
        setLoadingGuides(false);
      }
    };

    loadGuides();
  }, []);

  // Load about page data
  useEffect(() => {
    const loadAboutData = async () => {
      try {
        // About page data load
        const aboutResponse = await axios.get('https://travel-agency-server-lilac.vercel.app/api/about');
        if (aboutResponse.data.success) {
          setAboutPage(aboutResponse.data.aboutPage);
        }

        // About features load
        const featuresResponse = await axios.get('https://travel-agency-server-lilac.vercel.app/api/about-features');
        if (featuresResponse.data.success) {
          const activeFeatures = (featuresResponse.data.features || [])
            .filter(feature => feature.isActive === true)
            .sort((a, b) => a.order - b.order);
          setFeatures(activeFeatures);
        }
      } catch (error) {
        console.error('Error loading about data:', error);
        // Set default data
        setAboutPage({
          aboutImage: 'assets/img/about.jpg',
          aboutText: 'At Asian Travel Agency, we are dedicated to creating comfortable, reliable, and memorable travel experiences for every traveler. From peaceful natural escapes to culturally rich destinations, we carefully design each journey with attention to detail and personal care.',
          missionStatement: 'Our experienced team focuses on quality service, smooth planning, and customer satisfaction—so you can relax and enjoy every moment of your trip. Whether you are traveling for leisure, family vacations, or group tours, we ensure a seamless and enjoyable experience from start to finish.',
        });
        setFeatures([
          { title: 'First Class Flights', icon: 'fa fa-arrow-right text-primary me-2' },
          { title: 'Handpicked Hotels', icon: 'fa fa-arrow-right text-primary me-2' },
          { title: '5 Star Accommodations', icon: 'fa fa-arrow-right text-primary me-2' },
          { title: 'Latest Model Vehicles', icon: 'fa fa-arrow-right text-primary me-2' },
          { title: '150 Premium City Tours', icon: 'fa fa-arrow-right text-primary me-2' },
          { title: '24/7 Service', icon: 'fa fa-arrow-right text-primary me-2' }
        ]);
      } finally {
        setLoadingAbout(false);
      }
    };

    loadAboutData();
  }, []);

  useEffect(() => {
    const fetchDestinationGallery = async () => {
      try {
        setGalleryLoading(true);
        const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/destination-gallery');

        if (response.data.success) {
          setGalleryItems(response.data.galleryItems || []);
        }
      } catch (error) {
        console.error('Failed to fetch destination gallery:', error);
        setGalleryError('Failed to load destination gallery');
      } finally {
        setGalleryLoading(false);
      }
    };

    fetchDestinationGallery();
  }, []);

  // Load packages for booking form
  useEffect(() => {
    const loadBookingPackages = async () => {
      try {
        setBookingLoading(true);
        const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/packages');
        if (response.data.success && response.data.packages) {
          const activePackages = response.data.packages
            .filter(pkg => pkg.isActive === true)
            .sort((a, b) => a.order - b.order);
          setBookingPackages(activePackages);
        }
      } catch (error) {
        console.error('Error loading packages for booking:', error);
        setBookingPackages([
          { _id: '1', name: 'Bangladesh Cultural Tour' },
          { _id: '2', name: 'Sundarbans Adventure' },
          { _id: '3', name: 'Cox\'s Bazar Beach Vacation' }
        ]);
      } finally {
        setBookingLoading(false);
      }
    };

    loadBookingPackages();
  }, []);

  const handleBookingInputChange = (e) => {
    const { id, value } = e.target;
    setBookingFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!bookingFormData.name.trim() || !bookingFormData.email.trim() ||
      !bookingFormData.date.trim() || !bookingFormData.destination.trim()) {
      setBookingMessage({
        type: 'error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bookingFormData.email)) {
      setBookingMessage({
        type: 'error',
        text: 'Please enter a valid email address'
      });
      return;
    }

    try {
      setBookingSubmitting(true);
      setBookingMessage({ type: '', text: '' });

      // Get package name
      const selectedPackage = bookingPackages.find(pkg => pkg._id === bookingFormData.destination);
      const packageName = selectedPackage ? selectedPackage.name : bookingFormData.destination;

      const bookingData = {
        ...bookingFormData,
        packageName: packageName,
        status: 'pending',
        createdAt: new Date().toISOString()
      };

      const response = await axios.post('https://travel-agency-server-lilac.vercel.app/api/bookings', bookingData);

      if (response.data.success) {
        setBookingMessage({
          type: 'success',
          text: 'Booking submitted successfully! We will contact you soon.'
        });

        // Reset form
        setBookingFormData({
          name: '',
          email: '',
          date: '',
          destination: '',
          message: '',
          packageName: ''
        });
      } else {
        setBookingMessage({
          type: 'error',
          text: response.data.error || 'Failed to submit booking. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setBookingMessage({
        type: 'error',
        text: error.response?.data?.error || 'Network error. Please try again.'
      });
    } finally {
      setBookingSubmitting(false);
    }
  };

  // Split features into two columns for display
  const firstColumn = features.slice(0, Math.ceil(features.length / 2));
  const secondColumn = features.slice(Math.ceil(features.length / 2));

  if (loading) {
    return (
      <div className="container-xxl py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid bg-primary py-5 mb-5 hero-header">
        <div className="container py-5">
          <div className="row justify-content-center py-5">
            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
              <h1 className="display-3 text-white mb-3 animated slideInDown">
                Enjoy Your Vacation With Us
              </h1>
              <p className="fs-4 text-white mb-4 animated slideInDown">
                Where Every Road Leads to Beauty and Every Trip Becomes a Memory
              </p>
              <div className="position-relative w-75 mx-auto animated slideInDown">
                <input
                  className="form-control border-0 rounded-pill w-100 py-3 ps-4 pe-5"
                  type="text"
                  placeholder="Eg: Thailand"
                />
                <button
                  type="button"
                  className="btn btn-primary rounded-pill py-2 px-4 position-absolute top-0 end-0 me-2"
                  style={{ marginTop: 7 }}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Start */}
      {/* About Start - Dynamic Section */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="row g-5">
            <div
              className="col-lg-6 wow fadeInUp"
              data-wow-delay="0.1s"
              style={{ minHeight: 400 }}
            >
              <div className="position-relative h-100">
                <img
                  className="img-fluid position-absolute w-100 h-100"
                  src={aboutPage?.aboutImage || 'assets/img/about.jpg'}
                  alt="Asian Travel Agency Team"
                  style={{ objectFit: "cover", borderRadius: '10px' }}
                  onError={(e) => {
                    e.target.src = 'assets/img/about.jpg';
                  }}
                />
              </div>
            </div>
            <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.3s">
              <h6 className="section-title bg-white text-start text-primary pe-3">
                About Us
              </h6>
              <h1 className="mb-4">
                Welcome to <span className="text-primary">Asian Travel Agency</span>
              </h1>
              <p className="mb-3">
                {aboutPage?.aboutText || 'At Asian Travel Agency, we are dedicated to creating comfortable, reliable, and memorable travel experiences for every traveler. From peaceful natural escapes to culturally rich destinations, we carefully design each journey with attention to detail and personal care.'}
              </p>
              <p className="mb-4">
                {aboutPage?.missionStatement || 'Our experienced team focuses on quality service, smooth planning, and customer satisfaction—so you can relax and enjoy every moment of your trip. Whether you are traveling for leisure, family vacations, or group tours, we ensure a seamless and enjoyable experience from start to finish.'}
              </p>

              {/* Dynamic Features Section */}
              <div className="row gy-2 gx-4 mb-4">
                {loadingAbout ? (
                  <div className="col-12">
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted small">Loading features...</p>
                    </div>
                  </div>
                ) : features.length === 0 ? (
                  <div className="col-12 text-center">
                    <p className="text-muted">No features available</p>
                  </div>
                ) : (
                  <>
                    <div className="col-sm-6">
                      {firstColumn.map((feature, index) => (
                        <p key={index} className="mb-2 d-flex align-items-center">
                          <i className={feature.icon || 'fa fa-arrow-right text-primary me-2'}></i>
                          <span className="ms-2">{feature.title}</span>
                        </p>
                      ))}
                    </div>
                    <div className="col-sm-6">
                      {secondColumn.map((feature, index) => (
                        <p key={index + firstColumn.length} className="mb-2 d-flex align-items-center">
                          <i className={feature.icon || 'fa fa-arrow-right text-primary me-2'}></i>
                          <span className="ms-2">{feature.title}</span>
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <a className="btn btn-primary py-3 px-5 mt-2" href="/about">
                Read More
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* About End */}
      {/* About End */}

      {/* Service Start */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Services
            </h6>
            <h1 className="mb-5">Our Services</h1>
          </div>
          <div className="row g-4">
            {/* Card 1 - Accommodation */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.1s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-hotel text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Premium Accommodation</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Quality hotels/resorts with AC/non-AC rooms (as per package).
                    24-hour security, cleanliness, hot water, and generator facilities included.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 - Mid-Range Stays */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.3s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-bed text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Mid-Range Stays</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Comfortable and clean accommodations with all basic amenities.
                    Safe environment with regular room service and maintenance.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3 - Daily Meals */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.5s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-utensils text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Daily Meals</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Breakfast, lunch and dinner included. Local traditional cuisine
                    (seafood in Cox's Bazar, local chicken in Sundarbans) served.
                    Bottled drinking water provided.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 4 - Transportation */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.7s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-bus text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Transportation</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Luxury AC/non-AC bus or train tickets from Dhaka/Khulna.
                    Local transport includes microbuses, Chandor Gari, speedboats.
                    Airport/station pickup and drop facility.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 5 - Sightseeing Tours */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.1s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-binoculars text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Sightseeing Tours</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Visit all major tourist spots according to package duration
                    (like Saint Martin's Chera Dwip, Cox's Bazar's Inani).
                    All entry fees and parking charges included.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 6 - Travel Guides */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.3s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-user text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Expert Travel Guides</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Trained tour guides with knowledge of local culture and history.
                    Professional assistance throughout the tour for better experience.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 7 - Emergency Support */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.5s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-phone text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>24/7 Emergency Support</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Round-the-clock phone support. First aid kit available.
                    Arrangement for hospital transfer in emergencies.
                    Constant communication support.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 8 - Event Management */}
            <div className="col-lg-3 col-sm-6 wow fadeInUp" data-wow-delay="0.7s">
              <div className="service-item rounded pt-3" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease'
              }}>
                <div className="p-4" style={{ flex: '1 0 auto' }}>
                  <i className="fa fa-3x fa-cog text-primary mb-4" />
                  <h5 style={{ minHeight: '48px', marginBottom: '10px' }}>Event Management</h5>
                  <p style={{
                    marginBottom: '0',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '5',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    Professional event planning and management services for
                    corporate events, weddings, and special occasions during tours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Service End */}

      {/* Destination Start */}
      <div className="container-xxl py-5 destination">
        <div className="container">
          <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Destination
            </h6>
            <h1 className="mb-5">Popular Destination</h1>
          </div>

          {/* এখানে DestinationGallery কম্পোনেন্ট ব্যবহার করুন */}
          <DestinationGallery
            galleryItems={galleryItems}
            loading={galleryLoading}
            error={galleryError}
            onRetry={() => {
              // Retry function
              const fetchDestinationGallery = async () => {
                try {
                  setGalleryLoading(true);
                  const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/destination-gallery');

                  if (response.data.success) {
                    setGalleryItems(response.data.galleryItems || []);
                    setGalleryError('');
                  }
                } catch (error) {
                  console.error('Failed to fetch destination gallery:', error);
                  setGalleryError('Failed to load destination gallery');
                } finally {
                  setGalleryLoading(false);
                }
              };
              fetchDestinationGallery();
            }}
          />
        </div>
      </div>
      {/* Destination End */}

      {/* Package Start */}
      {/* Pre-made Packages Section */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Pre-made Packages
            </h6>
            <h1 className="mb-5">Awesome Travel Packages</h1>
            <p className="mb-5">Choose from our carefully curated travel packages or create your own custom trip</p>
          </div>

          <div className="row g-4 justify-content-center">
            {/* Dynamic Packages from Backend */}
            {packages.map((pkg, index) => (
              <div
                key={pkg._id}
                className="col-lg-4 col-md-6 wow fadeInUp"
                data-wow-delay={`${0.1 + (index * 0.2)}s`}
              >
                <div className="package-item shadow-sm h-100">
                  <div className="overflow-hidden position-relative">
                    <img
                      className="img-fluid w-100"
                      src={pkg.image || '/assets/img/package-1.jpg'}
                      alt={pkg.name}
                      style={{ height: '250px', objectFit: 'cover' }}
                    />
                    {pkg.rating && (
                      <div className="position-absolute top-0 end-0 m-3">
                        <span className="badge bg-warning text-dark">
                          <i className="fa fa-star me-1"></i>
                          {pkg.rating}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="d-flex border-bottom">
                    <small className="flex-fill text-center border-end py-2">
                      <i className="fa fa-map-marker-alt text-primary me-2" />
                      {pkg.location}
                    </small>
                    <small className="flex-fill text-center border-end py-2">
                      <i className="fa fa-calendar-alt text-primary me-2" />
                      {pkg.duration}
                    </small>
                    <small className="flex-fill text-center py-2">
                      <i className="fa fa-user text-primary me-2" />
                      {pkg.persons} Person{pkg.persons > 1 ? 's' : ''}
                    </small>
                  </div>
                  <div className="text-center p-4 d-flex flex-column h-100">
                    <div>
                      <h3 className="mb-2 text-primary">${pkg.price}</h3>
                      <h5 className="mb-3">{pkg.name}</h5>
                      <div className="mb-3">
                        {[...Array(5)].map((_, i) => (
                          <small
                            key={i}
                            className={`fa fa-star ${i < (pkg.rating || 5) ? 'text-warning' : 'text-secondary'}`}
                          />
                        ))}
                      </div>
                      <p className="mb-4 flex-grow-1">{pkg.description}</p>
                    </div>
                    <div className="d-flex justify-content-center mb-2 mt-auto">
                      <a
                        href="#"
                        className="btn btn-sm btn-primary px-3 border-end"
                        style={{ borderRadius: "30px 0 0 30px" }}
                      >
                        Read More
                      </a>
                      <a
                        href="#"
                        className="btn btn-sm btn-primary px-3"
                        style={{ borderRadius: "0 30px 30px 0" }}
                      >
                        Book Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Custom Package Box */}
            <div className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay="0.5s">
              <div
                className="package-item custom-package-box h-100"
                style={{
                  cursor: 'pointer',
                  border: '2px dashed #0d6efd',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                }}
                onClick={() => setShowForm(true)}
              >
                <div className="text-center p-4">
                  <div className="custom-package-icon mb-3">
                    <i className="fa fa-plus-circle fa-4x text-primary" />
                  </div>
                  <h3 className="text-primary mb-3">Customize Your Trip</h3>
                  <p className="text-muted mb-4">
                    Can't find your perfect package? Let us create a customized trip just for you!
                  </p>
                  <button className="btn btn-outline-primary btn-lg">
                    Create Custom Package
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Package Modal */}
      <CustomPackageForm
        showForm={showForm}
        setShowForm={setShowForm}
        setMessage={setMessage}
      />
      {/* Package End */}

      {/* Booking Start */}
      {/* Booking Start - Updated Dynamic Section */}
      <div className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
        <div className="container">
          {/* Message Alert */}
          {bookingMessage.text && (
            <div className={`alert alert-${bookingMessage.type} alert-dismissible fade show mb-4`}>
              <i className={`fas ${bookingMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'
                } me-2`}></i>
              {bookingMessage.text}
              <button
                type="button"
                className="btn-close"
                onClick={() => setBookingMessage({ type: '', text: '' })}
              ></button>
            </div>
          )}

          <div className="booking p-5" style={{
            backgroundImage: 'url(/assets/img/booking.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative'
          }}>
            {/* Overlay for better readability */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)', // Dark overlay
              zIndex: 1
            }}></div>

            <div className="row g-5 align-items-center position-relative" style={{ zIndex: 2 }}>
              <div className="col-md-6 text-white">
                <h6 className="text-warning text-uppercase">Booking</h6>
                <h1 className="text-white mb-4">Online Booking</h1>
                <p className="mb-4 text-white">
                  Planning your next adventure? Book with us for a seamless travel experience.
                  Our easy online booking system lets you reserve your dream tour in just a few clicks.
                </p>
                <p className="mb-4 text-white">
                  From tropical beaches to mountain treks, we offer curated packages that
                  cater to every traveler's needs. Secure your spot today and get ready
                  to create unforgettable memories with our expert guides and premium services.
                </p>
                <a className="btn btn-outline-warning py-3 px-5 mt-2" href="/packages">
                  View Packages
                </a>
              </div>
              <div className="col-md-6">
                <h1 className="text-white mb-4">Book A Tour</h1>
                <form onSubmit={handleBookingSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          id="name"
                          placeholder="Your Name"
                          value={bookingFormData.name}
                          onChange={handleBookingInputChange}
                          required
                        />
                        <label htmlFor="name" className="text-white">Your Name *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="email"
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          id="email"
                          placeholder="Your Email"
                          value={bookingFormData.email}
                          onChange={handleBookingInputChange}
                          required
                        />
                        <label htmlFor="email" className="text-white">Your Email *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="date"
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          id="date"
                          placeholder="Preferred Date"
                          value={bookingFormData.date}
                          onChange={handleBookingInputChange}
                          required
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <label htmlFor="date" className="text-white">Preferred Date *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <select
                          className="form-select bg-dark bg-opacity-75 text-white border-white"
                          id="destination"
                          value={bookingFormData.destination}
                          onChange={handleBookingInputChange}
                          required
                        >
                          <option value="" className="bg-dark">Select Package</option>
                          {bookingLoading ? (
                            <option value="" className="bg-dark" disabled>Loading packages...</option>
                          ) : (
                            bookingPackages.map((pkg) => (
                              <option key={pkg._id} value={pkg._id} className="bg-dark">
                                {pkg.name}
                              </option>
                            ))
                          )}
                        </select>
                        <label htmlFor="destination" className="text-white">Select Package *</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <textarea
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          placeholder="Special Request"
                          id="message"
                          style={{ height: 100 }}
                          value={bookingFormData.message}
                          onChange={handleBookingInputChange}
                        />
                        <label htmlFor="message" className="text-white">Special Request</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <button
                        className="btn btn-warning w-100 py-3"
                        type="submit"
                        disabled={bookingSubmitting}
                      >
                        {bookingSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                          </>
                        ) : (
                          'Book Now'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Booking End */}
      {/* Booking End */}

      {/* Process Start */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center pb-4 wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Process
            </h6>
            <h1 className="mb-5">3 Easy Steps</h1>
          </div>
          <div className="row gy-5 gx-4 justify-content-center">
            <div
              className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
              data-wow-delay="0.1s"
            >
              <div className="position-relative border border-primary pt-5 pb-4 px-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                  style={{ width: 100, height: 100 }}
                >
                  <i className="fa fa-globe fa-3x text-white" />
                </div>
                <h5 className="mt-4">Choose A Destination</h5>
                <hr className="w-25 mx-auto bg-primary mb-1" />
                <hr className="w-50 mx-auto bg-primary mt-0" />
                <p className="mb-0">
                  Browse our extensive collection of destinations worldwide.
                  From tropical paradises to historical landmarks, find the
                  perfect spot for your next adventure that matches your interests.
                </p>
              </div>
            </div>
            <div
              className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
              data-wow-delay="0.3s"
            >
              <div className="position-relative border border-primary pt-5 pb-4 px-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                  style={{ width: 100, height: 100 }}
                >
                  <i className="fa fa-sign fa-3x text-white" />
                </div>
                <h5 className="mt-4">Quality of Tour</h5>
                <hr className="w-25 mx-auto bg-primary mb-1" />
                <hr className="w-50 mx-auto bg-primary mt-0" />
                <p className="mb-0">
                  Experience premium travel with our carefully curated tours.
                  We ensure high-quality accommodations, expert guides, and
                  seamless itineraries for a memorable and comfortable journey.
                </p>
              </div>
            </div>
            <div
              className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
              data-wow-delay="0.5s"
            >
              <div className="position-relative border border-primary pt-5 pb-4 px-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                  style={{ width: 100, height: 100 }}
                >
                  <i className="fa fa-phone fa-3x text-white" />
                </div>
                <h5 className="mt-4">Contact With Us</h5>
                <hr className="w-25 mx-auto bg-primary mb-1" />
                <hr className="w-50 mx-auto bg-primary mt-0" />
                <p className="mb-0">
                  Get in touch with our travel experts for personalized assistance.
                  We provide 24/7 customer support to answer your queries and
                  help customize your perfect travel experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Process End */}

      {/* Team Start */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Team Member
            </h6>
            <h1 className="mb-5">Meet Our Officials</h1>
          </div>

          {loadingGuides ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading team members...</p>
            </div>
          ) : guides.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No team members available at the moment</p>
            </div>
          ) : (
            <div className="row g-4 justify-content-center">
              {guides.map((guide, index) => (
                <div key={guide._id} className="col-lg-3 col-md-6 wow fadeInUp" data-wow-delay={`${0.1 + index * 0.2}s`}>
                  <div className="team-item">
                    <div className="overflow-hidden">
                      <img
                        className="img-fluid"
                        src={guide.image || 'assets/img/default-guide.jpg'}
                        alt={guide.name}
                        style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = 'assets/img/default-guide.jpg';
                        }}
                      />
                    </div>
                    <div
                      className="position-relative d-flex justify-content-center"
                      style={{ marginTop: "-19px" }}
                    >
                      {guide.socialLinks?.facebook && (
                        <a className="btn btn-square mx-1" href={guide.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-facebook-f"></i>
                        </a>
                      )}
                      {guide.socialLinks?.twitter && (
                        <a className="btn btn-square mx-1" href={guide.socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-twitter"></i>
                        </a>
                      )}
                      {guide.socialLinks?.instagram && (
                        <a className="btn btn-square mx-1" href={guide.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-instagram"></i>
                        </a>
                      )}
                    </div>
                    <div className="text-center p-4">
                      <h5 className="mb-1 fw-bold text-dark">{guide.name}</h5>

                      {/* Designation underline - Green */}
                      <div className="designation mb-3">
                        <p className="text-success mb-0 fw-medium" style={{
                          fontSize: '0.9rem',
                          position: 'relative',
                          display: 'inline-block',
                          paddingBottom: '5px'
                        }}>
                          {guide.designation}
                          <span style={{
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '30px',
                            height: '2px',
                            backgroundColor: '#198754',
                            borderRadius: '2px'
                          }}></span>
                        </p>
                      </div>

                      {/* Bio in quote style - Green accents */}
                      {guide.bio && (
                        <div className="bio-quote mt-4">
                          <i className="fas fa-quote-left text-success me-1" style={{ fontSize: '0.8rem' }}></i>
                          <span className="text-muted small" style={{
                            fontStyle: 'normal',
                            fontSize: '0.85rem',
                            lineHeight: '1.6'
                          }}>
                            {guide.bio.length > 80 ? `${guide.bio.substring(0, 80)}...` : guide.bio}
                          </span>
                          <i className="fas fa-quote-right text-success ms-1" style={{ fontSize: '0.8rem' }}></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Team End */}

      {/* Testimonial Start */}
      <div className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
        <div className="container">
          <div className="text-center">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Testimonial
            </h6>
            <h1 className="mb-5">Our Clients Say!!!</h1>
          </div>
          <div className="owl-carousel testimonial-carousel position-relative">
            <div className="testimonial-item bg-white text-center border p-4">
              <img
                className="bg-white rounded-circle shadow p-1 mx-auto mb-3"
                src="assets/img/testimonial-1.jpg"
                style={{ width: 80, height: 80 }}
              />
              <h5 className="mb-0">John Doe</h5>
              <p>New York, USA</p>
              <p className="mb-0">
                Tempor erat elitr rebum at clita. Diam dolor diam ipsum sit diam
                amet diam et eos. Clita erat ipsum et lorem et sit.
              </p>
            </div>
            <div className="testimonial-item bg-white text-center border p-4">
              <img
                className="bg-white rounded-circle shadow p-1 mx-auto mb-3"
                src="assets/img/testimonial-2.jpg"
                style={{ width: 80, height: 80 }}
              />
              <h5 className="mb-0">John Doe</h5>
              <p>New York, USA</p>
              <p className="mt-2 mb-0">
                Tempor erat elitr rebum at clita. Diam dolor diam ipsum sit diam
                amet diam et eos. Clita erat ipsum et lorem et sit.
              </p>
            </div>
            <div className="testimonial-item bg-white text-center border p-4">
              <img
                className="bg-white rounded-circle shadow p-1 mx-auto mb-3"
                src="assets/img/testimonial-3.jpg"
                style={{ width: 80, height: 80 }}
              />
              <h5 className="mb-0">John Doe</h5>
              <p>New York, USA</p>
              <p className="mt-2 mb-0">
                Tempor erat elitr rebum at clita. Diam dolor diam ipsum sit diam
                amet diam et eos. Clita erat ipsum et lorem et sit.
              </p>
            </div>
            <div className="testimonial-item bg-white text-center border p-4">
              <img
                className="bg-white rounded-circle shadow p-1 mx-auto mb-3"
                src="assets/img/testimonial-4.jpg"
                style={{ width: 80, height: 80 }}
              />
              <h5 className="mb-0">John Doe</h5>
              <p>New York, USA</p>
              <p className="mt-2 mb-0">
                Tempor erat elitr rebum at clita. Diam dolor diam ipsum sit diam
                amet diam et eos. Clita erat ipsum et lorem et sit.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Testimonial End */}

      {/* WhatsApp and Messenger Icons */}
      <WhatsAppMessengerIcons />

      {/* Custom Package Box CSS */}
      <style>{`
        .package-item {
          transition: all 0.3s ease;
          border-radius: 10px;
          overflow: hidden;
          background: white;
        }
        
        .package-item:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        
        .custom-package-box {
          transition: all 0.3s ease;
        }
        
        .custom-package-box:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(13, 110, 253, 0.2);
          border-color: #0d6efd !important;
        }
        
        .custom-package-icon {
          transition: transform 0.3s ease;
        }
        
        .custom-package-box:hover .custom-package-icon {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  )
}